import {computed, defineComponent, type PropType, ref, watch} from 'vue';
import {useModalEventsUtil} from "@/app/shared/helpers/modal-events-util.ts";
import * as yup from 'yup';
import {toTypedSchema} from "@vee-validate/yup";
import {type AnnouncementFormData, useAnnouncementStore} from "@/app/stores/announcement-store.ts";
import {AnnouncementDto, AnnouncementSeverity, AthumiService} from "@/api/enterprise-gateway-api.proxy.ts";
import {useForm} from "vee-validate";
import {storeToRefs} from "pinia";
import DateUtil from "@/app/shared/helpers/date-util.ts";
import {first} from "lodash";

export const addMessageModal = 'add-message-modal';


const FORM_INITIAL_VALUES: AnnouncementFormData = {
    message: '',
    title: '',
    dateFrom: [''],
    dateTo: ['']
}

const ANNOUNCEMENT_VALIDATION_SCHEMA = toTypedSchema(
    yup.object({
        message: yup.string().required('Bericht is verplicht'),
        title: yup.string().required('Titel is verplicht'),
        dateFrom: yup.array()
            .of(yup.string().required('Begindatum is verplicht'))
            .min(1, 'Begindatum is verplicht'),
        dateTo: yup.array().notRequired()
    })
);


export default defineComponent({
    name: 'AegAddAnnouncement',
    props: {
        announcement: {
            type: Object as PropType<AnnouncementDto>,
            required: false
        }
    },
    emits: ['on-save', 'on-delete'],
    setup: function (props, {emit}) {
        const modalEventsUtil = useModalEventsUtil(addMessageModal, {
            onClosed() {
                announcementToEdit.value = undefined;
                resetForm();
            },
        });
        const announcementStore = useAnnouncementStore();
        const {isSavingAnnouncement, isDeletingAnnouncement} = storeToRefs(announcementStore);
        const announcementToEdit = ref<AnnouncementDto | undefined>(undefined);
        const {handleSubmit, defineField, errors, setValues, meta, resetForm} = useForm<AnnouncementFormData>({
            validationSchema: ANNOUNCEMENT_VALIDATION_SCHEMA,
            initialValues: FORM_INITIAL_VALUES
        });

        const [dateFrom, dateFromProps] = defineField('dateFrom');
        const [dateTo, dateToProps] = defineField('dateTo');
        const [title, titleProps] = defineField('title');
        const [message, messageProps] = defineField('message');

        const isNewMessage = computed(() => {
            return !announcementToEdit.value?.id;
        });

        const invalidForm = computed((): boolean => {
            return !meta.value.valid || isSavingAnnouncement.value;
        });
        const handleFormSubmit = async (formData: AnnouncementFormData) => {
            const announcementToSave = AnnouncementDto.fromJS({
                id: announcementToEdit.value?.id,
                severity: AnnouncementSeverity.Warning,
                from: DateUtil.formatForApi(first(formData.dateFrom), 'YYYY-MM-DD 00:00:00'),
                to: DateUtil.formatForApi(first(formData.dateTo), 'YYYY-MM-DD 00:00:00'),
                title: formData.title,
                message: formData.message,
            });

            const result = await announcementStore.saveAnnouncement(announcementToSave);
            modalEventsUtil.triggerModalToggle();
            emit('on-save', result);
        }

        const onCloseModal = () => {
            modalEventsUtil.triggerModalToggle();
        }

        const removeAnnouncement = async () => {
            if (!announcementToEdit.value) return;
            await announcementStore.deleteAnnouncement(announcementToEdit.value?.id!)
            modalEventsUtil.triggerModalToggle();
            emit('on-delete', announcementToEdit.value?.id!);
        }

        const announcementChanged = (newAnnouncement: AnnouncementDto | undefined) => {
            if (!newAnnouncement) return;
            announcementToEdit.value = newAnnouncement;
            setValues({
                message: newAnnouncement.message,
                title: newAnnouncement.title,
                dateFrom: newAnnouncement.from ? [newAnnouncement.from] : [''],
                dateTo: newAnnouncement.to ? [newAnnouncement.to] : ['']
            }, true);
        }

        const onSubmit = handleSubmit(handleFormSubmit);
        watch(() => props.announcement, announcementChanged, {immediate: true, deep: true});

        return {
            addMessageModal,
            dateFrom,
            dateFromProps,
            dateTo,
            dateToProps,
            title,
            titleProps,
            message,
            messageProps,
            errors,
            isNewMessage,
            invalidForm,
            isDeletingAnnouncement,
            onSubmit,
            onCloseModal,
            removeAnnouncement
        }
    }
});
